import { useState } from "react";
import { Modal } from "./ui";
import { BACKEND_URL } from "../lib/api";
import PDFViewer from "./PDFViewer";
import { FileText, Download, ExternalLink, FileSpreadsheet, Presentation, CheckCircle } from "lucide-react";

/**
 * Unified report button.
 * - PDF: muestra preview inline (PDFViewer) + permite descargar.
 * - XLSX / PPTX: descarga directa (NUNCA conversión vía LibreOffice).
 * LibreOffice fue eliminado por completo del sistema.
 */
export default function ReportButton({
  collection,
  endpoint,           // legacy fallback for /api/pdf/module/...
  customUrl,          // path that accepts ?fmt=...&preview=... (e.g. "/reports/executive-consolidated")
  formats: customFormats,
  filename = "reporte",
  label = "Reporte",
  extraBody = {},
  testId = "report-btn",
}) {
  const [open, setOpen] = useState(false);
  const [observations, setObservations] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFmt, setSelectedFmt] = useState(null);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  function close() {
    setOpen(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setSelectedFmt(null); setError(""); setObservations(""); setSuccessMsg("");
  }

  function showSuccess(msg) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 2500);
  }

  async function fetchReport(fmt, asPreview) {
    const token = localStorage.getItem("token");
    const audit = localStorage.getItem("audit_tenant_id");
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
    if (audit) headers["X-Audit-Tenant-Id"] = audit;
    let url;
    if (customUrl) {
      url = `${BACKEND_URL}/api${customUrl}?fmt=${fmt}${asPreview ? "&preview=true" : ""}`;
    } else if (collection) {
      url = `${BACKEND_URL}/api/reports/module/${collection}?fmt=${fmt}${asPreview ? "&preview=true" : ""}`;
    } else {
      url = `${BACKEND_URL}/api${endpoint}`;
    }
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ observations, ...extraBody }),
    });
    if (!res.ok) throw new Error((await res.text()).slice(0, 240) || res.statusText);
    return await res.blob();
  }

  /** Selecciona un formato. PDF muestra preview inline; otros se descargan directo. */
  async function selectFmt(fmt) {
    setError(""); setLoading(true);
    setSelectedFmt(fmt);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    try {
      if (fmt === "pdf") {
        const blob = await fetchReport("pdf", true);
        const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        setPreviewUrl(url);
      } else {
        // Descarga directa para XLSX / PPTX / DOCX — sin conversión.
        const blob = await fetchReport(fmt, false);
        triggerDownload(blob, fmt);
        showSuccess(`Reporte ${fmt.toUpperCase()} descargado correctamente`);
      }
    } catch (e) {
      setError(e.message || "Error generando el reporte");
    } finally { setLoading(false); }
  }

  function triggerDownload(blob, fmt) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${filename}.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 800);
  }

  async function download() {
    if (!selectedFmt) return;
    setLoading(true);
    try {
      const blob = await fetchReport(selectedFmt, false);
      triggerDownload(blob, selectedFmt);
      showSuccess(`Reporte ${selectedFmt.toUpperCase()} descargado correctamente`);
    } catch (e) {
      setError("No se pudo descargar: " + e.message);
    } finally { setLoading(false); }
  }

  const formats = customFormats || (collection || customUrl
    ? [
        { key: "pdf",  label: "PDF",   icon: <FileText size={14} />,        color: "btn-primary" },
        { key: "xlsx", label: "Excel", icon: <FileSpreadsheet size={14} />, color: "btn" },
        { key: "pptx", label: "PPT",   icon: <Presentation size={14} />,    color: "btn" },
      ]
    : [
        { key: "pdf",  label: "PDF",   icon: <FileText size={14} />,        color: "btn-primary" },
      ]);

  return (
    <>
      <button className="btn btn-sm" onClick={() => setOpen(true)} data-testid={testId}>
        <FileText size={14} /> {label}
      </button>
      <Modal open={open} onClose={close} title={label} size={previewUrl ? "xl" : "lg"} testId={`${testId}-modal`}>
        <div className="grid gap-3">
          <div>
            <label className="label">Observaciones del coordinador (opcional)</label>
            <textarea
              className="textarea"
              rows={3}
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Comentarios, hallazgos relevantes, compromisos..."
              data-testid={`${testId}-observations`}
            />
          </div>

          <div>
            <label className="label">Formato del reporte</label>
            <div className="flex gap-2 flex-wrap">
              {formats.map((f) => (
                <button
                  key={f.key}
                  className={`btn ${selectedFmt === f.key ? "btn-primary" : ""}`}
                  onClick={() => selectFmt(f.key)}
                  disabled={loading}
                  data-testid={`${testId}-fmt-${f.key}`}
                  title={f.key === "pdf" ? "Vista previa + descarga" : "Descarga directa"}
                >
                  {loading && selectedFmt === f.key ? <span className="spinner" /> : f.icon} {f.label}
                  {f.key !== "pdf" && <Download size={11} style={{ marginLeft: 4, opacity: 0.7 }} />}
                </button>
              ))}
              <span className="text-xs text-dim self-center ml-2">
                PDF: vista previa inline · Excel/PPT: descarga directa
              </span>
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-3 p-6 justify-center text-dim" data-testid={`${testId}-loading`}>
              <span className="spinner" /> {selectedFmt && selectedFmt !== "pdf" ? "⏳ Generando…" : "Generando reporte…"}
            </div>
          )}

          {error && (
            <div className="card" style={{ background: "rgba(229,57,53,0.10)", border: "1px solid var(--danger)" }} data-testid={`${testId}-error`}>
              {error}
            </div>
          )}

          {successMsg && (
            <div className="card" style={{ background: "rgba(0,200,83,0.10)", border: "1px solid #00C853" }} data-testid={`${testId}-success`}>
              <CheckCircle size={14} style={{ display: "inline", marginRight: 6, color: "#00C853" }} />
              {successMsg}
            </div>
          )}

          {previewUrl && selectedFmt === "pdf" && !loading && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-dim">Vista previa — PDF</div>
                <div className="flex gap-2">
                  <button className="btn btn-sm" onClick={() => selectFmt("pdf")} data-testid={`${testId}-reload`}>Recargar</button>
                  <button className="btn btn-sm" onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")} data-testid={`${testId}-open-tab`}>
                    <ExternalLink size={12} /> Abrir en nueva pestaña
                  </button>
                </div>
              </div>
              <PDFViewer url={previewUrl} testId={`${testId}-preview-iframe`} height="70vh" />
            </div>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <button className="btn" onClick={close}>Cerrar</button>
            <button
              className="btn btn-primary"
              onClick={download}
              disabled={!selectedFmt || loading}
              data-testid={`${testId}-download`}
            >
              <Download size={14} /> Descargar {selectedFmt?.toUpperCase() || ""}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
